import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, StatusPill, KpiCard } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bug, Lightbulb, ThumbsUp, MoreHorizontal, ExternalLink, CheckCircle2, XCircle, Clock, Eye, MailCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RowBin, ClearArea } from "@/components/admin/Purge";

const STATUS_ORDER = ["all", "open", "triaged", "resolved", "wontfix"];
const CAT_ICON = { bug: Bug, idea: Lightbulb, compliment: ThumbsUp, other: MoreHorizontal };
const CAT_HUE = {
  bug: "bg-rose-50 text-rose-700 ring-rose-200",
  idea: "bg-amber-50 text-amber-700 ring-amber-200",
  compliment: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  other: "bg-slate-100 text-slate-700 ring-slate-200",
};

export default function Feedback() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [replyNote, setReplyNote] = useState("");
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    api.get("/admin/feedback/digest/preview").then(r => setDigestPreview(r.data)).catch(() => {});
  }, []);

  const sendDigestNow = async () => {
    if (!window.confirm(`Send digest email to ${digestPreview?.admin_recipients?.length || 0} admins now?`)) return;
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/feedback/digest/send");
      const sent = (r.data?.results || []).filter(x => x.sent).length;
      toast.success(`Digest sent — ${sent}/${r.data?.recipients} admins delivered. (Others previewed in logs due to Resend trial.)`);
      // refresh preview's last_run
      const p = await api.get("/admin/feedback/digest/preview");
      setDigestPreview(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/feedback?status=${filter}`);
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status, withNote = false) => {
    const payload = withNote && replyNote.trim() ? { status, reply_note: replyNote.trim() } : { status };
    const r = await api.patch(`/admin/feedback/${id}`, payload);
    if (r.data?.notified) {
      toast.success("Status updated — user notified by email.");
    } else if (["resolved", "wontfix"].includes(status)) {
      toast.success("Status updated. (Email skipped — Resend trial restrictions or already notified.)");
    } else {
      toast.success("Status updated.");
    }
    setReplyNote("");
    refresh();
    if (viewing?.id === id) setViewing({ ...viewing, status });
  };

  // Counters across all statuses
  const [counts, setCounts] = useState({ open: 0, triaged: 0, resolved: 0, wontfix: 0 });
  useEffect(() => {
    api.get("/admin/feedback?status=all&limit=500").then((r) => {
      const c = { open: 0, triaged: 0, resolved: 0, wontfix: 0 };
      (r.data || []).forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, [rows.length]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionTitle title="Feedback inbox" subtitle="Bug reports, ideas and compliments submitted by signed-in users via the in-app widget." />
        <div className="flex items-center gap-2">
        <ClearArea area="feedback" onDone={refresh} label="Clear feedback" />
        <Button
          onClick={sendDigestNow}
          disabled={sendingDigest || !digestPreview}
          variant="outline"
          className="rounded-full"
          data-testid="fb-send-digest"
        >
          <MailCheck className="size-4 mr-1.5" />
          {sendingDigest ? "Sending…" : "Send digest now"}
        </Button>
        </div>
      </div>

      {digestPreview && (
        <Card className="rounded-xl border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-soft" data-testid="fb-digest-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="inline-grid place-items-center size-8 rounded-md bg-slate-900 text-white"><MailCheck className="size-4" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Weekly digest</div>
                  <div className="text-sm text-slate-900 font-medium">
                    {digestPreview.stats.total} report{digestPreview.stats.total === 1 ? "" : "s"} in the last 7 days
                    {digestPreview.stats.avg_resolve_hours != null && (
                      <span className="text-slate-500 font-normal"> · avg resolve {digestPreview.stats.avg_resolve_hours}h</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 font-mono text-right">
                <div>Recipients · {digestPreview.admin_recipients?.length || 0} admins</div>
                <div>Last sent · {digestPreview.last_run ? new Date(digestPreview.last_run).toLocaleString("en-GB") : "never"}</div>
                <div>Next auto-run · Monday 08:00 UTC</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-4 gap-4">
        <KpiCard label="Open" value={counts.open} testid="fb-kpi-open" />
        <KpiCard label="Triaged" value={counts.triaged} testid="fb-kpi-triaged" />
        <KpiCard label="Resolved" value={counts.resolved} testid="fb-kpi-resolved" />
        <KpiCard label="Won't fix" value={counts.wontfix} testid="fb-kpi-wontfix" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`fb-filter-${s}`}
            className={`rounded-full text-xs font-medium px-3 py-1.5 border transition-all capitalize ${
              filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            {s === "wontfix" ? "Won't fix" : s}
          </button>
        ))}
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center">
              <CheckCircle2 className="size-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No feedback in this filter — quiet day!</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {rows.map((r) => {
                const Icon = CAT_ICON[r.category] || MoreHorizontal;
                const hue = CAT_HUE[r.category] || CAT_HUE.other;
                return (
                  <li key={r.id} className="p-5 hover:bg-slate-50/60 transition-colors" data-testid={`fb-row-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <span className={`inline-grid place-items-center size-8 rounded-md ring-1 shrink-0 ${hue}`}>
                        <Icon className="size-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${hue}`}>{r.category}</span>
                          <StatusPill status={r.status} />
                          <span className="text-[11px] font-mono text-slate-400">{r.user_email}</span>
                          <span className="text-[11px] text-slate-400">·</span>
                          <span className="text-[11px] text-slate-500 capitalize">{r.user_role}</span>
                          <span className="text-[11px] text-slate-400 ml-auto font-mono tabular-nums">{new Date(r.created_at).toLocaleString("en-GB")}</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-900 leading-relaxed line-clamp-2">{r.description}</p>
                        <div className="mt-2.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                          {r.page_url && (
                            <a href={r.page_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                              <ExternalLink className="size-3" />
                              <span className="truncate max-w-[260px]">{(() => { try { return new URL(r.page_url).pathname; } catch { return r.page_url; } })()}</span>
                            </a>
                          )}
                          <span className="font-mono">{r.viewport}</span>
                          {r.console_errors?.length > 0 && (
                            <span className="text-amber-600 font-medium">{r.console_errors.length} console error{r.console_errors.length === 1 ? "" : "s"}</span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col gap-1.5">
                        <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => setViewing(r)} data-testid={`fb-view-${r.id}`}>
                          <Eye className="size-3 mr-0.5" /> View
                        </Button>
                        {r.status !== "resolved" && (
                          <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => setStatus(r.id, "resolved")} data-testid={`fb-resolve-${r.id}`}>
                            <CheckCircle2 className="size-3 mr-0.5 text-emerald-600" /> Resolve
                          </Button>
                        )}
                        {r.status === "open" && (
                          <Button variant="ghost" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => setStatus(r.id, "triaged")} data-testid={`fb-triage-${r.id}`}>
                            <Clock className="size-3 mr-0.5" /> Triage
                          </Button>
                        )}
                        <div className="self-end"><RowBin area="feedback" id={r.id} name={r.category} onDone={refresh} /></div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-[680px]" data-testid="fb-detail-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 capitalize">{viewing.category} from {viewing.user_email}</DialogTitle>
                <DialogDescription className="font-mono text-[11px]">{viewing.page_url}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-900 leading-relaxed whitespace-pre-wrap">{viewing.description}</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11.5px] font-mono space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Role</span><span>{viewing.user_role}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Viewport</span><span>{viewing.viewport}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">User Agent</span><span className="truncate ml-2 max-w-[380px] text-right">{viewing.user_agent}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Submitted</span><span>{new Date(viewing.created_at).toLocaleString("en-GB")}</span></div>
                </div>
                {viewing.console_errors?.length > 0 && (
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Console errors captured</div>
                    <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] font-mono text-amber-900 space-y-1 max-h-48 overflow-auto">
                      {viewing.console_errors.map((e, i) => <li key={i} className="leading-relaxed">{e}</li>)}
                    </ul>
                  </div>
                )}

                {viewing.user_notified_at && (
                  <div className="flex items-center gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <MailCheck className="size-4" />
                    User was notified by email on {new Date(viewing.user_notified_at).toLocaleString("en-GB")}.
                  </div>
                )}

                {!viewing.user_notified_at && viewing.status === "open" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="reply-note" className="text-xs font-medium">Optional reply to the user (included in the resolution email)</Label>
                    <Textarea
                      id="reply-note"
                      data-testid="fb-reply-note"
                      value={replyNote}
                      onChange={(e) => setReplyNote(e.target.value)}
                      rows={3}
                      placeholder="e.g. We've shipped a fix on the leads pagination — let us know if you spot anything else."
                      maxLength={1000}
                    />
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <Badge variant="outline" className="rounded-full">Current status: <span className="ml-1 capitalize">{viewing.status}</span></Badge>
                  <div className="ml-auto flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setStatus(viewing.id, "triaged")} className="rounded-full" data-testid="fb-detail-triage"><Clock className="size-3.5 mr-1" />Triage</Button>
                    <Button variant="outline" size="sm" onClick={() => setStatus(viewing.id, "resolved", true)} className="rounded-full" data-testid="fb-detail-resolve">
                      <CheckCircle2 className="size-3.5 mr-1 text-emerald-600" />
                      {replyNote.trim() ? "Resolve & notify" : "Resolve"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setStatus(viewing.id, "wontfix", true)} className="rounded-full" data-testid="fb-detail-wontfix"><XCircle className="size-3.5 mr-1 text-rose-600" />Won't fix</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
